import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Press } from '@dorixona/ui'
import { BackButton } from '../../ui/BackButton'
import { Icon } from '../../ui/Icon'
import { Failed, Loading } from '../../ui/Load'
import { useCurrentLocation } from '../../app/location'
import { useMapsOn } from '../../app/settings'
import { useSession } from '../../app/session'
import { useCart } from '../cart/CartProvider'
import { useStores } from '../stores/api'
import { StoresMap } from '../stores/StoresMap'
import { distanceText, hoursText } from '../stores/model'
import { useCheckoutOptions } from './api'
import { draftStore, rememberShip, rememberStore } from './draft'
import { fulfilling, pickupStore } from './model'

/**
 * Аптека самовывоза на карте. На карте — аптеки, куда можно прийти за этим
 * заказом, с точками и метрами от сервера; номер на булавке — место в его
 * списке «ближе — выше», синяя точка — сам покупатель, «Где я» подводит к нему.
 * Нажали булавку — под картой её адрес, расстояние и режим работы; «Забрать
 * здесь» возвращает на оформление с этой аптекой.
 */
export function PharmacyScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation(['checkout', 'common', 'stores'])
  const { isAuthenticated } = useSession()
  const cart = useCart()
  const enabled = isAuthenticated && cart.lines.length > 0
  const here = useCurrentLocation(enabled)
  const options = useCheckoutOptions(enabled, null, here.data ?? null)
  // Режим работы — из списка аптек сети: в ответе оформления его нет.
  const stores = useStores()
  // На карте — только аптеки, что соберут весь заказ, а не вся сеть из `/stores`.
  const pickup = fulfilling(options.data?.pickup ?? [])
  const [selected, setSelected] = useState(0)
  const [meFocus, setMeFocus] = useState(0)
  const [locating, setLocating] = useState(false)
  const [locateFailed, setLocateFailed] = useState(false)
  const current = selected || pickupStore(pickup, draftStore())

  const pins = pickup.map((store) => ({
    id: store.storeId,
    name: store.name,
    lat: store.lat,
    lng: store.lng,
  }))
  const row = pickup.find((store) => store.storeId === current)
  const network = stores.data?.find((store) => store.id === current)
  const mapsOn = useMapsOn()

  /** «Где я»: спрашиваем точку заново — доступ могли дать только что. */
  function locate() {
    setLocating(true)
    setLocateFailed(false)
    void here.refetch().then((result) => {
      setLocating(false)
      if (result.data) setMeFocus((n) => n + 1)
      else setLocateFailed(true)
    })
  }

  function confirm(id: number) {
    rememberShip('pickup')
    rememberStore(id)
    // Пришли с оформления — возвращаемся туда же, не плодя записей истории.
    if (location.key === 'default') void navigate('/checkout', { replace: true })
    else void navigate(-1)
  }

  // Карты выключены в админке — выбирать на карте нечего: аптеку выбирают
  // списком в оформлении.
  if (mapsOn === false) return <Navigate to="/checkout" replace />

  return (
    <section className="screen checkout-wrap">
      <div className="mhead">
        <BackButton to="/checkout" />
        <h1>{t('checkout:pharmacyPage.title')}</h1>
      </div>

      {options.isPending ? <Loading what={t('common:state.loading')} count={2} /> : null}
      {options.isError ? (
        <Failed
          error={options.error}
          onRetry={() => {
            void options.refetch()
          }}
        />
      ) : null}

      {options.data ? (
        <>
          <div className="addr-map-box">
            <div className="st-map ph-map">
              <StoresMap
                stores={pins}
                selected={current || null}
                onSelect={setSelected}
                me={here.data ?? null}
                meFocus={meFocus}
              />
            </div>
            <Press
              squish="icon"
              className="addr-locate"
              aria-label={t('checkout:pharmacyPage.locate')}
              disabled={locating}
              aria-busy={locating || undefined}
              onPress={locate}
            >
              <Icon name="i-locate" size={20} />
            </Press>
          </div>
          {locateFailed ? (
            <p className="acc-note acc-note-err" role="status">
              {t('checkout:pharmacyPage.locateFailed')}
            </p>
          ) : null}

          {row ? (
            <div className="card ph-chosen">
              <div className="opt">
                <Icon name="i-pin" size={18} />
                <div>
                  <div className="t1">
                    {row.district}
                    {row.distanceM !== null ? (
                      <span className="co-dist num">
                        {distanceText(row.distanceM, t, i18n.language)}
                      </span>
                    ) : null}
                  </div>
                  <div className="t2">{row.address}</div>
                  {network ? (
                    <div className="t2">
                      {t('checkout:hours', { hours: hoursText(network, t) })}
                    </div>
                  ) : null}
                </div>
                <div className="tag">{t(row.openNow ? 'checkout:open' : 'checkout:closed')}</div>
              </div>
            </div>
          ) : (
            <p className="auth-note">{t('checkout:pharmacyPage.hint')}</p>
          )}

          {pickup.length === 0 ? <p className="auth-note">{t('checkout:noPharmacy')}</p> : null}

          <Button
            disabled={!row}
            // Нажать можно только при выбранной аптеке.
            onPress={() => {
              confirm(current)
            }}
          >
            {t('checkout:pharmacyPage.pick')}
          </Button>
        </>
      ) : null}
    </section>
  )
}
